<template>
  <div class="multiselect-combo-box">
    <div class="input-container">
      <div class="selected-pills w-[calc(100%-24px)]">
        <Pill
          v-for="(option, index) in selectedOptions"
          :key="index"
          color="grey"
          tabindex="0"
          class="ma-4"
          @keydownEnter="removeOption(option)"
        >
          <span class="flex items-center">
            {{ option.display }}
            <span class="cursor-pointer" @click.stop="removeOption(option)">
              <CloseIconMini class="ml-[5px]" :width="9" :height="9" color="#6E6F74" />
            </span>
          </span>
        </Pill>
        <input
          id="multiselect-input"
          v-model="searchTerm"
          type="text"
          :placeholder="selectedOptions.length ? null : labelName"
          @focus="showDropdown = true"
          @input="filterOptions"
          @keydown.down="onArrowDown"
          @keydown.up="onArrowUp"
          @keydown.enter="onEnter"
        />
      </div>
      <div class="text-field-icon">
        <search-outline-icon color="#4B4C52" />
      </div>
    </div>
    <ul v-if="showDropdown" class="dropdown" role="listbox">
      <li v-if="filteredOptions.length === 0" class="dropdown-item !cursor-not-allowed" role="option">
        <span class="text-grey-text-subdued">No options</span>
      </li>
      <li
        v-for="(option, i) in filteredOptions"
        :key="option.value"
        class="dropdown-item"
        :class="{
          selected: selectedOptions.some((selected) => selected.value === option.value),
          'is-active': i === arrowCounter,
        }"
        @keyup.enter="toggleOption(option)"
        @click="toggleOption(option)"
      >
        {{ option.display }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import SearchOutlineIcon from '@/graphics/icons/SearchOutlineIcon.vue';
import Pill from '../pill/Pill.vue';
import CloseIconMini from '@/graphics/icons/CloseIconMini.vue';

export default {
  name: 'MultiselectComboBox',
  components: { Pill, SearchOutlineIcon, CloseIconMini },
  props: {
    options: {
      type: Array as () => { value: string; display: string }[],
      required: true,
    },
    preselectedOptions: {
      type: Array as () => string[],
      default: () => [],
    },
    labelName: {
      type: String,
      default: 'Select options',
    },
  },
  data() {
    return {
      searchTerm: '',
      selectedOptions: [] as { value: string; display: string }[],
      showDropdown: false,
      arrowCounter: -1,
      filteredOptions: [],
    };
  },
  watch: {
    preselectedOptions: {
      handler(newVal) {
        const updatedOptions = this.options.filter((option) => newVal.includes(option.value));
        if (JSON.stringify(updatedOptions) !== JSON.stringify(this.selectedOptions)) {
          this.selectedOptions = updatedOptions;
        }
        this.filterOptions();
      },
      immediate: true,
    },
    arrowCounter(newVal) {
      const active = this.$el.querySelectorAll('li')[newVal];
      if (active && newVal === 0) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    },
    selectedOptions: {
      handler() {
        this.$emit('selectedOptions', this.selectedOptions);
      },
      deep: true,
    },
    options: {
      immediate: true,
      handler(newOptions) {
        this.filteredOptions = newOptions.filter((option) => !this.preselectedOptions.includes(option.value));
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.arrowCounter = -1;
        this.showDropdown = false;
      }
    },
    toggleOption(option) {
      const isSelected = this.selectedOptions.some((selected) => selected.value === option.value);
      if (isSelected) {
        this.removeOption(option);
      } else {
        this.selectOption(option);
      }
    },
    filterOptions() {
      const term = this.searchTerm?.toLowerCase();
      if (term.length && !this.showDropdown) {
        this.showDropdown = true;
      }
      this.filteredOptions = this.options.filter(
        (option) =>
          option.display.toLowerCase().includes(term) && !this.selectedOptions.some((selected) => selected.value === option.value)
      );
    },
    selectOption(option) {
      this.selectedOptions.push(option);
      this.searchTerm = '';
      this.showDropdown = false;
      this.filterOptions();
    },
    removeOption(option) {
      this.selectedOptions = this.selectedOptions.filter((selected) => selected.value !== option.value);
      this.filterOptions();
    },
    onArrowDown() {
      if (this.showDropdown === false) {
        this.showDropdown = true;
      }
      if (this.arrowCounter < this.filteredOptions.length - 1) {
        this.arrowCounter += 1;
      } else if (this.arrowCounter === this.filteredOptions.length - 1) {
        this.arrowCounter = 0;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1;
      }
    },
    onEnter(e) {
      e.preventDefault();
      if (this.arrowCounter > -1) {
        this.selectedOptions.push(this.filteredOptions[this.arrowCounter]);
        this.searchTerm = '';
        this.showDropdown = false;
        this.filterOptions();
        this.arrowCounter = -1;
        this.showDropdown = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.multiselect-combo-box {
  position: relative;
  width: 100%;
}

.input-container {
  background: white;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 8px 16px;
  min-height: 56px;
  border-radius: 4px;
}

.selected-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  -webkit-box-align: center;
  align-items: center;
  display: flex;
  flex: 1 1 0%;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

input {
  border: none;
  outline: none;
  flex-grow: 1;
  background: none;
  width: 30px;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid #ccc;
  background: white;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.dropdown-item {
  padding: 10px;
  cursor: pointer;
  border: 2px solid transparent;
  &.is-active {
    background: theme('colors.grey.background.contrast');
    color: theme('colors.brand.contrast');
    border: 2px dotted theme('colors.brand.contrast');
  }
}

.dropdown-item:hover {
  background: #f0f0f0;
}
</style>
